import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Skeleton,
    Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import BoltIcon from '@mui/icons-material/Bolt';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import HomeIcon from '@mui/icons-material/Home';
import TvIcon from '@mui/icons-material/Tv';
import PercentIcon from '@mui/icons-material/Percent';
import CalculateIcon from '@mui/icons-material/Calculate';
import ReceiptIcon from '@mui/icons-material/Receipt';

const BillBreakdownModal = ({ open, onClose, houseId }) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [breakdownData, setBreakdownData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open && houseId) {
            setLoading(true);
            setError(null);

            axios.get(`/data/cost/currentmonth/${houseId}`)
                .then((response) => {
                    setBreakdownData(response.data);
                    setLoading(false);
                })
                .catch((error) => {
                    setError(error.message || 'Failed to load bill breakdown');
                    setLoading(false);
                });
        } else if (!open) {
            setBreakdownData(null);
            setError(null);
        }
    }, [open, houseId]);

    // Get current billing period
    const currentDate = new Date();
    const billingPeriod = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

    // Extract values from API response (support both snake_case and camelCase)
    const getValue = (...fields) => {
        if (!breakdownData) return 0;
        for (const field of fields) {
            if (breakdownData[field] !== undefined && breakdownData[field] !== null) {
                return parseFloat(breakdownData[field]) || 0;
            }
        }
        return 0;
    };

    const commissionCharges = getValue('commission_charges', 'commissionCharges');
    const regulationCost = getValue('regulation_cost', 'regulationCost');
    const variousCharges = getValue('various_charges', 'variousCharges');
    const municipalTaxes = getValue('municipal_cost', 'municipalTaxes');
    const ertCost = getValue('ert_cost', 'ertCost');
    const vat = getValue('vat', 'VAT');

    // Electricity Cost = commission_charges + regulation_cost
    const electricityCost = commissionCharges + regulationCost;

    // Calculate total cost
    const calculateTotal = () => {
        if (!breakdownData) return 0;
        const total = electricityCost + variousCharges + municipalTaxes + ertCost + vat;
        return total.toFixed(2);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: '25px',
                    width: '500px',
                    maxWidth: '90vw',
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                        sx={{
                            backgroundColor: theme.palette.primary.light,
                            width: 40,
                            height: 40
                        }}
                    >
                        <ReceiptIcon sx={{ color: 'white' }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            Bill Breakdown
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {billingPeriod}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {loading ? (
                    <Box>
                        <Skeleton variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 2 }} />
                        <Skeleton variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 2 }} />
                        <Skeleton variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 2 }} />
                        <Skeleton variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 2 }} />
                        <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
                    </Box>
                ) : error ? (
                    <Typography color="error" sx={{ p: 2 }}>{error}</Typography>
                ) : (
                    <Box>
                        {/* Electricity Cost with sub-items - always expanded */}
                        <Box
                            sx={{
                                marginBottom: '12px',
                                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                                borderRadius: '12px',
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '16px',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar
                                        sx={{
                                            backgroundColor: '#FF9800',
                                            width: 40,
                                            height: 40
                                        }}
                                    >
                                        <BoltIcon sx={{ color: 'white', fontSize: 20 }} />
                                    </Avatar>
                                    <Typography variant="body1" fontWeight={600}>
                                        Electricity Cost
                                    </Typography>
                                </Box>
                                <Typography variant="body1" fontWeight={700} sx={{ color: theme.palette.text.primary }}>
                                    €{electricityCost.toFixed(2)}
                                </Typography>
                            </Box>
                            <Box sx={{ paddingLeft: '40px', paddingRight: '16px', paddingBottom: '12px' }}>
                                {commissionCharges > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingY: '8px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar
                                                sx={{
                                                    backgroundColor: '#9C27B0',
                                                    width: 24,
                                                    height: 24
                                                }}
                                            >
                                                <BusinessIcon sx={{ color: 'white', fontSize: 12 }} />
                                            </Avatar>
                                            <Typography variant="body2" color="text.secondary">
                                                Commission Charges
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" fontWeight={600}>
                                            €{commissionCharges.toFixed(2)}
                                        </Typography>
                                    </Box>
                                )}
                                {regulationCost > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingY: '8px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar
                                                sx={{
                                                    backgroundColor: '#9C27B0',
                                                    width: 24,
                                                    height: 24
                                                }}
                                            >
                                                <SettingsIcon sx={{ color: 'white', fontSize: 12 }} />
                                            </Avatar>
                                            <Typography variant="body2" color="text.secondary">
                                                Regulation Charges
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" fontWeight={600}>
                                            €{regulationCost.toFixed(2)}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Various Charges */}
                        {variousCharges > 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '16px',
                                    marginBottom: '12px',
                                    backgroundColor: 'rgba(117, 117, 117, 0.1)',
                                    borderRadius: '12px',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar
                                        sx={{
                                            backgroundColor: '#757575',
                                            width: 40,
                                            height: 40
                                        }}
                                    >
                                        <SettingsIcon sx={{ color: 'white', fontSize: 20 }} />
                                    </Avatar>
                                    <Typography variant="body1" fontWeight={600}>
                                        Various Charges
                                    </Typography>
                                </Box>
                                <Typography variant="body1" fontWeight={700} sx={{ color: theme.palette.text.primary }}>
                                    €{variousCharges.toFixed(2)}
                                </Typography>
                            </Box>
                        )}

                        {/* Municipal Taxes - always show */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '16px',
                                marginBottom: '12px',
                                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                borderRadius: '12px',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                    sx={{
                                        backgroundColor: '#4CAF50',
                                        width: 40,
                                        height: 40
                                    }}
                                >
                                    <HomeIcon sx={{ color: 'white', fontSize: 20 }} />
                                </Avatar>
                                <Typography variant="body1" fontWeight={600}>
                                    Municipal Taxes
                                </Typography>
                            </Box>
                            <Typography variant="body1" fontWeight={700} sx={{ color: theme.palette.text.primary }}>
                                €{municipalTaxes.toFixed(2)}
                            </Typography>
                        </Box>

                        {/* ERT - always show */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '16px',
                                marginBottom: '12px',
                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                borderRadius: '12px',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                    sx={{
                                        backgroundColor: '#F44336',
                                        width: 40,
                                        height: 40
                                    }}
                                >
                                    <TvIcon sx={{ color: 'white', fontSize: 20 }} />
                                </Avatar>
                                <Typography variant="body1" fontWeight={600}>
                                    ERT
                                </Typography>
                            </Box>
                            <Typography variant="body1" fontWeight={700} sx={{ color: theme.palette.text.primary }}>
                                €{ertCost.toFixed(2)}
                            </Typography>
                        </Box>

                        {/* VAT */}
                        {vat > 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '16px',
                                    marginBottom: '12px',
                                    backgroundColor: 'rgba(0, 188, 212, 0.1)',
                                    borderRadius: '12px',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar
                                        sx={{
                                            backgroundColor: '#00BCD4',
                                            width: 40,
                                            height: 40
                                        }}
                                    >
                                        <PercentIcon sx={{ color: 'white', fontSize: 20 }} />
                                    </Avatar>
                                    <Typography variant="body1" fontWeight={600}>
                                        VAT
                                    </Typography>
                                </Box>
                                <Typography variant="body1" fontWeight={700} sx={{ color: theme.palette.text.primary }}>
                                    €{vat.toFixed(2)}
                                </Typography>
                            </Box>
                        )}

                        {/* Total Cost Section */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '20px',
                                marginTop: '16px',
                                backgroundColor: theme.palette.primary.light + '20',
                                borderRadius: '12px',
                                border: `2px solid ${theme.palette.primary.main}`,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                    sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        width: 48,
                                        height: 48
                                    }}
                                >
                                    <CalculateIcon sx={{ color: 'white', fontSize: 24 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" fontWeight={700}>
                                        Total Cost
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Final amount to pay
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="h5" fontWeight={700} sx={{ color: theme.palette.primary.main }}>
                                €{calculateTotal()}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default BillBreakdownModal;

